import { IApplicationData } from 'types/applications';

import HeroImage from 'assets/applications/third/hero.webp';
import FirstBlockImage from 'assets/applications/app-3.0.webp';

import AutomationIcon from 'assets/applications/icons/automation.svg';
import DeveloperModeIcon from 'assets/applications/icons/developer-mode.svg';

import BrainIcon from 'assets/applications/icons/why-choose-us/brain.svg';
import TickBoxIcon from 'assets/applications/icons/why-choose-us/tick-box.svg';
import CircleAroundIcon from 'assets/applications/icons/why-choose-us/circle-around.svg';
import VectorIcon from 'assets/applications/icons/why-choose-us/vector.svg';
import ThomasKImage from 'assets/avatars/ThomasK.svg';
import MichaelGrantImage from 'assets/avatars/MichaelGrant.svg';

import PlanesInAirportImage from 'assets/common/images/aeronautics-main.webp';
import DozenOfPlanesImage from 'assets/common/images/aeronautics-topic-2.webp';
import ThreePlanesImage from 'assets/customer-cases/airbus-up-next/three-planes.webp';
import PlaneImage from 'assets/customer-cases/airbus-up-next/plane.webp';

import { airbusUpNextCustomerCaseMeta } from 'constants/pages';

export const thirdApplicationData: IApplicationData = {
  hero: {
    title: 'Automatic & Intelligent Dataset Enhancement with Simulation',
    description: `Collecting and labeling real-world data is time-consuming, costly, and rarely captures the edge cases your AI needs to handle complex operational environments. AILiveSim augments your existing datasets with intelligent, AI-driven simulation — generating synthetic data that fills gaps, improves coverage, and boosts model performance.
    `,
    videoSrc: 'https://ailivesim.com/Videos/AILiveSim_Navy.mp4',
    imageSrc: HeroImage,
  },
  firstTextImageData: {
    title: 'Data Collection Challenges',
    description: `
      Real-world data collection is slow, expensive, and often incomplete. By combining your existing datasets with 
      AI-powered simulation, AILiveSim generates diverse, high-quality synthetic data that improves coverage, 
      accuracy, and robustness — all while reducing costs.
    `,
    imageSrc: FirstBlockImage,
    listItems: [
      'High costs and delays in real-world data acquisition',
      'Manual scenario creation limits diversity and realism',
      'Difficulty capturing edge cases and defining comprehensive test sets',
      'Incomplete or biased datasets that reduce model generalization',
    ],
  },
  informationData: {
    title: 'What Our Dataset Enhancement Solution Delivers',
    description:
      'AILiveSim brings AI-powered simulation into your dataset enhancement workflow — on-premise or in the cloud, fully automated, and scalable to any project size. It generates synthetic data that complements real-world datasets, increases diversity, and strengthens AI model performance across a variety of operational conditions.',
    items: [
      {
        imageSrc: AutomationIcon,
        title: 'Automated Synthetic Data Generation for Better Coverage',
        description:
          'Eliminate manual effort by generating high-quality, diverse datasets on demand.',
      },
      {
        imageSrc: DeveloperModeIcon,
        title: 'Scenario Diversity to Improve Model Generalization',
        description:
          'Introduce rare and edge-case conditions to prepare AI for real-world unpredictability.',
      },
    ],
    listItems: [
      'AI-powered simulation to automatically generate complementary synthetic data',
      'Augmentation of real-world datasets with varied weather, lighting, and environmental conditions',
      'Scenario diversity to expose models to edge cases and rare conditions',
      'Seamless integration into your existing MLOps or data pipeline',
    ],
    listTitle: 'Key Capabilities of AILiveSim for Dataset Enhancement',
  },
  whyChooseUsData: {
    title: 'Why Teams Choose AILiveSim for Smarter Dataset Enhancement',
    blueBoxTitle:
      'Make simulation a key part of your AI data strategy — close coverage gaps, reduce costs, and deploy AI that performs reliably in the real world.',
    items: [
      {
        title: 'Boost Model Robustness Across Scenarios',
        description:
          'Train AI to handle real-world unpredictability by integrating continuous dataset enhancement directly into your MLOps pipeline.',
        imageSrc: CircleAroundIcon,
      },
      {
        title: 'Reduce Dependency on Costly Data Campaigns',
        description:
          'Cut the need for expensive, time-consuming real-world data collection by generating high-quality synthetic datasets on demand.',
        imageSrc: VectorIcon,
      },
      {
        title: 'Fill Dataset Gaps Automatically',
        description:
          'Identify and resolve data blind spots with AI-driven simulation that ensures full coverage across weather, lighting, and environmental variations.',
        imageSrc: BrainIcon,
      },
      {
        title: 'Enhance AI Performance in Real-World Conditions',
        description:
          'Enhance AI performance and generalization in diverse operational environments by enriching datasets with synthetic edge cases and rare scenarios.',
        imageSrc: TickBoxIcon,
      },
    ],
  },
  carouselData: [
    {
      authorImage: MichaelGrantImage,
      authorName: 'Michael Grant',
      authorRole: 'Innovation Lead at a major maritime OEM',
      text: '“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”',
    },
    {
      authorImage: ThomasKImage,
      authorName: 'Thomas K.',
      authorRole: 'Program Lead, Helicopter OEM',
      text: `"AILiveSim allowed us to simulate the unpredictable—from night-time flights near power lines to low-visibility urban environments. It's the only solution that offered the
        variability and realism our project demanded."`,
    },
  ],
  secondTextImageData: {
    title: 'Customer Case: Revolutionizing Automated Taxiing in Aviation',
    description: `
      From weather variability to airport traffic complexity, AILiveSim allowed comprehensive, repeatable testing that cut costs and shortened development cycles.
    `,
    images: {
      firstImage: {
        imageSrc: PlanesInAirportImage,
        altText: 'Planes in airport',
      },
      secondImage: {
        imageSrc: ThreePlanesImage,
        altText: 'Three planes in airport',
      },
      thirdImage: {
        imageSrc: PlaneImage,
        altText: 'Single plane in airport',
      },
      fourthImage: {
        imageSrc: DozenOfPlanesImage,
        altText: 'Dozen of planes in airport',
      },
    },
    link: airbusUpNextCustomerCaseMeta.url,
  },
};
